/**
 * OtaLogo — a neutral, self-contained OTA logo tile.
 *
 * IMPLEMENTATION NOTE (official brand assets):
 * ---------------------------------------------
 * AVOS does not ship official Booking.com / Airbnb / MakeMyTrip / Agoda /
 * Expedia / Trip.com brand image files (we must not fabricate logos or hotlink
 * third-party assets). This component renders a tasteful NEUTRAL placeholder
 * tile — the OTA's initials on a subtle brand-accent background — so the UI is
 * complete and on-brand today.
 *
 * To drop in official logos later WITHOUT touching call sites:
 *   1. Add each asset under `packages/web/public/ota/<code>.svg` (or .png), e.g.
 *      `packages/web/public/ota/booking_com.svg`, served at `/ota/<code>.svg`.
 *   2. Add the code to `HAS_OFFICIAL_ASSET` below.
 *   3. This component will then render `<img src="/ota/<code>.svg">` instead of
 *      the placeholder. Do NOT recolour official logos (§4).
 *
 * SSR-safe + pure: no clock/DOM reads; deterministic output for a given code.
 */
import type { ReactElement } from "react";

/**
 * OTA codes for which an official asset file exists under `/ota/<code>.svg`.
 * EMPTY today (placeholders used). Add a code here once the real file is added.
 */
const HAS_OFFICIAL_ASSET: ReadonlySet<string> = new Set<string>(["trip_com"]);

/** A subtle brand-accent background per OTA (used ONLY for the placeholder tile). */
const OTA_ACCENT: Record<string, string> = {
  airbnb: "#ff5a5f",
  booking_com: "#003580",
  makemytrip: "#eb2226",
  agoda: "#5c2d91",
  expedia: "#00355f",
  trip_com: "#287dfa",
};

/** Two-letter initials for the placeholder tile from the OTA display name. */
function initials(name: string): string {
  const words = name.replace(/[^A-Za-z0-9 ]/g, " ").trim().split(/\s+/);
  if (words.length === 0 || words[0] === "") {
    return "OTA";
  }
  if (words.length === 1) {
    return words[0]!.slice(0, 2).toUpperCase();
  }
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}

/** Props for {@link OtaLogo}. */
export interface OtaLogoProps {
  /** The AVOS OTA code (e.g. "booking_com"). */
  readonly code: string;
  /** The OTA display name (for initials + alt text). */
  readonly name: string;
  /** Pixel size of the square logo area (catalogue 40–48, cards 40). */
  readonly size?: number;
}

/**
 * Render the OTA logo — the official asset when present, else a neutral
 * placeholder tile. Kept visually contained + aligned; never recolours an
 * official logo.
 */
export function OtaLogo({ code, name, size = 44 }: OtaLogoProps): ReactElement {
  if (HAS_OFFICIAL_ASSET.has(code)) {
    return (
      <span
        className="ota-logo ota-logo--img"
        style={{ width: `${size}px`, height: `${size}px` }}
      >
        <img src={`/ota/${code}.svg`} alt={`${name} logo`} width={size} height={size} />
      </span>
    );
  }
  const accent = OTA_ACCENT[code] ?? "var(--color-forest)";
  return (
    <span
      className="ota-logo ota-logo--placeholder"
      style={{ width: `${size}px`, height: `${size}px`, background: accent }}
      role="img"
      aria-label={`${name} logo`}
      title={name}
    >
      <span aria-hidden="true" className="ota-logo__initials">
        {initials(name)}
      </span>
    </span>
  );
}

export default OtaLogo;
